import * as React from 'react';
import {
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
  type RadioGroupProps as AriaRadioGroupProps,
  type RadioProps as AriaRadioProps,
} from 'react-aria-components';

import { cx } from '../../utils/cx';

export interface IRadioGroupContextType {
  size?: 'sm' | 'md';
}

const RadioGroupContext = React.createContext<IRadioGroupContextType | null>(null);

export interface IRadioButtonBaseProps {
  size?: 'sm' | 'md';
  className?: string;
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
}

export const RadioButtonBase = ({
  className,
  isFocusVisible,
  isSelected,
  isDisabled,
  size = 'sm',
}: IRadioButtonBaseProps) => {
  return (
    <div
      className={cx(
        'bg-primary ring-primary flex size-4 min-h-4 min-w-4 cursor-pointer appearance-none items-center justify-center rounded-full ring-1 ring-inset',
        size === 'md' && 'size-5 min-h-5 min-w-5',
        isSelected && !isDisabled && 'bg-brand-solid ring-bg-brand-solid',
        isDisabled && 'border-disabled bg-disabled_subtle cursor-not-allowed',
        isFocusVisible && 'outline-focus-ring outline-2 outline-offset-2',
        className
      )}
    >
      <div
        className={cx(
          'bg-fg-white transition-inherit-all size-1.5 rounded-full opacity-0',
          size === 'md' && 'size-2',
          isDisabled && 'bg-fg-disabled_subtle',
          isSelected && 'opacity-100'
        )}
      />
    </div>
  );
};
RadioButtonBase.displayName = 'RadioButtonBase';

interface IRadioButtonProps extends AriaRadioProps {
  size?: 'sm' | 'md';
  label?: React.ReactNode;
  hint?: React.ReactNode;
  ref?: React.Ref<HTMLLabelElement>;
}

export const RadioButton = ({ label, hint, className, size = 'sm', ...ariaRadioProps }: IRadioButtonProps) => {
  const context = React.useContext(RadioGroupContext);

  size = context?.size ?? size;

  const sizes = {
    sm: {
      root: 'gap-2',
      textWrapper: '',
      label: 'text-sm font-semibold',
      hint: 'text-sm',
    },
    md: {
      root: 'gap-3',
      textWrapper: 'gap-0.5',
      label: 'text-md font-semibold',
      hint: 'text-md',
    },
  };

  return (
    <AriaRadio
      {...ariaRadioProps}
      className={(renderProps) =>
        cx(
          'flex items-start',
          renderProps.isDisabled && 'cursor-not-allowed',
          sizes[size].root,
          typeof className === 'function' ? className(renderProps) : className
        )
      }
    >
      {({ isSelected, isDisabled, isFocusVisible }) => (
        <>
          <RadioButtonBase
            size={size}
            isSelected={isSelected}
            isDisabled={isDisabled}
            isFocusVisible={isFocusVisible}
            className={label || hint ? 'mt-0.5' : ''}
          />
          {(label || hint) && (
            <div className={cx('inline-flex flex-col', sizes[size].textWrapper)}>
              {label && <p className={cx('text-secondary select-none', sizes[size].label)}>{label}</p>}
              {hint && (
                <span className={cx('text-tertiary', sizes[size].hint)} onClick={(event) => event.stopPropagation()}>
                  {hint}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </AriaRadio>
  );
};
RadioButton.displayName = 'RadioButton';

interface IRadioButtonCardProps extends AriaRadioProps {
  label: React.ReactNode;
  hint?: React.ReactNode;
  ref?: React.Ref<HTMLLabelElement>;
}

/**
 * Card-style radio option. The whole card is the click target; the radio dot
 * sits in the trailing slot. The selected state swaps the 1px secondary
 * border for a 2px brand border and bumps the label to primary-semibold.
 *
 * Figma: Mantle - UX/UI nodes `4169:73126` (unselected) and `4169:73146`
 * (selected). Sizes inherited from the parent `RadioGroup` are ignored — the
 * card always renders at the same size.
 */
export const RadioButtonCard = ({ label, hint, className, ...ariaRadioProps }: IRadioButtonCardProps) => {
  return (
    <AriaRadio
      {...ariaRadioProps}
      className={(renderProps) =>
        cx(
          'bg-primary shadow-xs flex w-full cursor-pointer items-start gap-2 rounded-xl p-4',
          renderProps.isSelected ? 'ring-bg-brand-solid ring-2 ring-inset' : 'ring-secondary ring-1 ring-inset',
          renderProps.isDisabled && 'bg-disabled_subtle cursor-not-allowed',
          renderProps.isFocusVisible && 'outline-focus-ring outline-2 outline-offset-2',
          typeof className === 'function' ? className(renderProps) : className
        )
      }
    >
      {({ isSelected, isDisabled, isFocusVisible }) => (
        <>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className={cx('text-md select-none', isSelected ? 'text-primary font-semibold' : 'text-secondary')}>
              {label}
            </p>
            {hint && <span className="text-tertiary text-sm">{hint}</span>}
          </div>
          <div className="flex shrink-0 items-center justify-center pt-0.5">
            <RadioButtonBase
              size="sm"
              isSelected={isSelected}
              isDisabled={isDisabled}
              isFocusVisible={isFocusVisible}
            />
          </div>
        </>
      )}
    </AriaRadio>
  );
};
RadioButtonCard.displayName = 'RadioButtonCard';

interface IRadioGroupProps extends IRadioGroupContextType, AriaRadioGroupProps {
  children: React.ReactNode;
  className?: string;
}

export const RadioGroup = ({ children, className, size = 'sm', ...props }: IRadioGroupProps) => {
  return (
    <RadioGroupContext.Provider value={{ size }}>
      <AriaRadioGroup {...props} className={cx('flex flex-col gap-4', className)}>
        {children}
      </AriaRadioGroup>
    </RadioGroupContext.Provider>
  );
};
